import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../components/seo/SeoHead';
import { SUPPORT_EMAIL } from '../config/site';
import './ContactUs.css';

async function copyEmailToClipboard(email: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(email);
    return true;
  } catch {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = email;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  }
}

const ContactUs: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(t);
  }, []);

  const handleCopyEmail = async () => {
    setCopyError(false);
    const ok = await copyEmailToClipboard(SUPPORT_EMAIL);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } else {
      setCopyError(true);
      window.setTimeout(() => setCopyError(false), 3000);
    }
  };

  return (
    <div className={`contact-page ${visible ? 'contact-page--visible' : ''}`}>
      <SeoHead
        title="Contact Us"
        description={`Contact DHH Collab support for Desi Hip-Hop platform questions, feedback, or help. Email ${SUPPORT_EMAIL}.`}
        path="/contact"
        keywords="DHH Collab contact, Desi Hip Hop support, dhhcollab help"
      />

      <div className="contact-page__bg" aria-hidden>
        <span className="contact-page__blob contact-page__blob--1" />
        <span className="contact-page__blob contact-page__blob--2" />
        <span className="contact-page__grid" />
      </div>

      <div className="contact-page__inner max-w-4xl mx-auto container-padding">
        <header className="contact-page__header">
          <span className="contact-page__eyebrow">We&apos;re here to help</span>
          <h1 className="contact-page__title">Contact Us</h1>
          <p className="contact-page__lead">
            If you have questions, please do check out the{' '}
            <Link to="/faq" className="contact-page__faq-link">
              FAQ page
            </Link>{' '}
            first — and if you can&apos;t find an answer there, mail us your issue and
            we&apos;ll help you out.
          </p>
        </header>

        <div className="contact-page__layout">
          <div className="contact-page__card contact-page__card--primary">
            <div className="contact-page__icon-wrap">
              <span className="contact-page__icon-ring" aria-hidden />
              <div className="contact-page__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
            </div>
            <h2 className="contact-page__card-title">Email Support</h2>
            <p className="contact-page__card-text">
              Send us an email with your query and, if needed, attach screenshots to help us assist you better.
            </p>
            <button
              type="button"
              className={`contact-page__email ${copied ? 'contact-page__email--copied' : ''} ${copyError ? 'contact-page__email--error' : ''}`}
              onClick={handleCopyEmail}
              aria-label={copied ? 'Email copied to clipboard' : `Copy ${SUPPORT_EMAIL} to clipboard`}
            >
              {copied ? 'Copied!' : copyError ? 'Copy failed — try again' : SUPPORT_EMAIL}
            </button>
            <p className="contact-page__copy-hint" aria-live="polite">
              {copied ? 'Paste into your email app to reach us.' : 'Click to copy the support email'}
            </p>
          </div>

          <div className="contact-page__aside">
            <div className="contact-page__tip contact-page__tip--1">
              <span className="contact-page__tip-icon" aria-hidden>
                💬
              </span>
              <div>
                <h3>Quick response</h3>
                <p>We typically reply within 1–2 business days.</p>
              </div>
            </div>
            <div className="contact-page__tip contact-page__tip--2">
              <span className="contact-page__tip-icon" aria-hidden>
                📎
              </span>
              <div>
                <h3>Include details</h3>
                <p>Screenshots and your username help us resolve issues faster.</p>
              </div>
            </div>
            <div className="contact-page__tip contact-page__tip--3">
              <span className="contact-page__tip-icon" aria-hidden>
                🎤
              </span>
              <div>
                <h3>Collab questions</h3>
                <p>Ask about beats, verses, accounts, or anything on the platform.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="contact-page__wave-deco" aria-hidden>
          {Array.from({ length: 24 }).map((_, i) => {
            const h = 25 + Math.abs(Math.sin(i * 0.55) * 55) + (i % 5) * 4;
            return (
              <span
                key={i}
                className="contact-page__wave-bar"
                style={
                  {
                    '--bar-i': i,
                    '--bar-height': `${h}%`,
                  } as React.CSSProperties
                }
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
